// Accounting's lists in Blueprint (MoneyLists.tsx): the filters as the pages offer them, and the input
// each page sends invoices.list and payments.list for them. Kept apart so it's tested without a screen.
import type { FilterChoice } from '@flowology/ui-web';

export type InvoiceShow = 'all' | 'draft' | 'sent' | 'owed' | 'overdue' | 'paid' | 'void';
export type PaymentShow = 'all' | 'deposited' | 'undeposited' | 'refunded' | 'voided';

/** The prototype's segment (accounting/invoices), with Owed for anything still owed. */
export const INVOICE_SHOWS: readonly FilterChoice<InvoiceShow>[] = [
  ['all', 'All'],
  ['draft', 'Draft'],
  ['sent', 'Sent'],
  ['owed', 'Owed'],
  ['overdue', 'Overdue'],
  ['paid', 'Paid'],
  ['void', 'Void'],
];

/** Payments by where they stand: in the bank, waiting for a deposit, money given back, voided. */
export const PAYMENT_SHOWS: readonly FilterChoice<PaymentShow>[] = [
  ['all', 'All'],
  ['deposited', 'In the bank'],
  ['undeposited', 'To deposit'],
  ['refunded', 'Refunded'],
  ['voided', 'Voided'],
];

export interface ListFilter<S extends string> {
  show: S;
  search: string;
  from: string;
  to: string;
}

/** How many rows a page reads, and "Show more" reads next. */
export const PAGE = 50;

/** What a page sends for its filter: only what's set, a search trimmed, dates only when whole. */
export function listInput<S extends string>(
  f: ListFilter<S>,
  after?: string | null,
): Record<string, string | number> {
  const date = (d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d);
  const search = f.search.trim();
  return {
    limit: PAGE,
    ...(f.show !== 'all' ? { state: f.show } : {}),
    ...(search ? { search: search.slice(0, 100) } : {}),
    ...(date(f.from) ? { from: f.from } : {}),
    ...(date(f.to) ? { to: f.to } : {}),
    ...(after ? { after } : {}),
  };
}

/** A from after a to keeps nothing: said beside the dates, and the page reads nothing for it. */
export const rangeWrong = (f: { from: string; to: string }): boolean => !!f.from && !!f.to && f.from > f.to;

/** The part of Books where each month-end check is fixed. */
export const FIX_PART: Record<string, 'setup' | 'banking' | 'payables' | 'payroll' | 'tax'> = {
  events_posted: 'setup',
  bank_reconciled: 'banking',
  funds_deposited: 'banking',
  bills_approved: 'payables',
  payroll_posted: 'payroll',
  tax_returns_filed: 'tax',
};
